import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { MenuItemForm } from "@/components/menu/MenuItemForm";
import { MenuItem, MenuItemFormData } from "@/types/menu";
import { useMenuStore } from "@/store/menuStore";
import { useMenuCacheUtils } from "@/services/menu/menuItem/fetchMenuItems";

interface EditMenuItemDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  menuItem: MenuItem;
  onItemUpdated?: () => void;
}

export function EditMenuItemDialog({
  open,
  onOpenChange,
  menuItem,
  onItemUpdated,
}: EditMenuItemDialogProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { updateMenuItem, isLoading, error } = useMenuStore();
  const { invalidateMenuItems } = useMenuCacheUtils();

  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (error) {
      toast({
        title: t("editMenuItemDialog.errorMessage"),
        description: error,
        variant: "destructive",
      });
    }
  }, [error, toast, t]);

  const handleSubmit = async (data: MenuItemFormData) => {
    setIsSubmitting(true);

    const updates = {
      name: data.name,
      description: data.description,
      price: parseFloat(data.price),
      available: data.available,
      image_url: data.image_url,
      store_id: data.store_id,
    };

    try {
      const updatedItem = await updateMenuItem(
        menuItem.id,
        updates,
        data.categoryIds,
        data.tags
      );

      if (updatedItem) {
        // Invalidate menu items cache
        if (data.store_id) {
          invalidateMenuItems(data.store_id);
        }
        
        toast({
          title: t("editMenuItemDialog.successMessage"),
          description: `${data.name} ${t(
            "editMenuItemDialog.successDescription"
          )}`,
        });

        if (onItemUpdated) {
          onItemUpdated();
        }

        onOpenChange(false);
      } else {
        toast({
          title: t("editMenuItemDialog.errorMessage"),
          description: t("editMenuItemDialog.errorDescription"),
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error updating menu item:", error);
      toast({
        title: t("editMenuItemDialog.errorMessage"),
        description: t("editMenuItemDialog.errorDescription"),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Don't render if menuItem is null or undefined
  if (!menuItem) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("editMenuItemDialog.title")}</DialogTitle>
          <DialogDescription>
            {t("editMenuItemDialog.description")}
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="space-y-4">
            <div className="h-10 animate-pulse bg-muted rounded-md"></div>
            <div className="h-10 animate-pulse bg-muted rounded-md"></div>
            <div className="h-20 animate-pulse bg-muted rounded-md"></div>
          </div>
        ) : (
          <MenuItemForm
            initialData={{
              id: menuItem.id,
              name: menuItem.name,
              description: menuItem.description || "",
              price: menuItem.price.toString(),
              available: menuItem.available,
              image_url: menuItem.image_url || "",
              store_id: menuItem.store_id,
              categoryIds: menuItem.categoryIds || [],
              tags: menuItem.tags || [],
            }}
            onSubmit={handleSubmit}
            submitButtonText={t("editMenuItemDialog.submitButton")}
            isSubmitting={isSubmitting}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
